/**
 * DrillDownContext — Global state for the drill-down drawer system.
 *
 * Usage:
 *   const { open, close } = useDrillDown();
 *   open('payroll_cost', { company_id: 'xxx', period_month: 3 });
 *
 * Wrap App (or each dashboard page) in <DrillDownProvider>.
 * The drawer renders itself via the provider — no per-card portal needed.
 */

import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';

// ── Types ────────────────────────────────────────────────────

export interface DrillDownFilters {
  date_from?: string;
  date_to?: string;
  company_id?: string;
  department_id?: string;
  status?: string;
  period_month?: number;
  period_year?: number;
  category?: string;
  search?: string;
  [key: string]: unknown;
}

export interface DrillDownBreadcrumb {
  label: string;
  metricKey: string;
  filters: DrillDownFilters;
}

export interface DrillDownSession {
  metricKey: string;
  filters: DrillDownFilters;
  /** Value shown in the KPI card that was clicked */
  kpiValue?: string | number;
  /** Human-readable context for audit notice */
  displayTitle: string;
  breadcrumbs: DrillDownBreadcrumb[];
  openedAt: string;
}

interface DrillDownContextValue {
  session: DrillDownSession | null;
  isOpen: boolean;
  /** Open the drawer for a given metric key with optional pre-set filters */
  open: (metricKey: string, filters?: DrillDownFilters, kpiValue?: string | number) => void;
  /** Navigate into a child metric while preserving breadcrumb history */
  drillInto: (metricKey: string, filters?: DrillDownFilters, kpiValue?: string | number) => void;
  /** Step back one level in the breadcrumb */
  stepBack: () => void;
  /** Close and reset */
  close: () => void;
  /** Update filters within the current session */
  updateFilters: (filters: Partial<DrillDownFilters>) => void;
}

// ── Context ───────────────────────────────────────────────────

const DrillDownContext = createContext<DrillDownContextValue | null>(null);

// ── Provider ──────────────────────────────────────────────────

export function DrillDownProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<DrillDownSession | null>(null);

  const open = useCallback(
    (metricKey: string, filters: DrillDownFilters = {}, kpiValue?: string | number) => {
      setSession({
        metricKey,
        filters,
        kpiValue,
        displayTitle: metricKey, // registry will override with pretty title
        breadcrumbs: [],
        openedAt: new Date().toISOString(),
      });
    },
    [],
  );

  const drillInto = useCallback(
    (metricKey: string, filters: DrillDownFilters = {}, kpiValue?: string | number) => {
      setSession(prev => {
        if (!prev) return null;
        const crumb: DrillDownBreadcrumb = {
          label: prev.displayTitle,
          metricKey: prev.metricKey,
          filters: prev.filters,
        };
        return {
          metricKey,
          filters: { ...prev.filters, ...filters },
          kpiValue,
          displayTitle: metricKey,
          breadcrumbs: [...prev.breadcrumbs, crumb],
          openedAt: new Date().toISOString(),
        };
      });
    },
    [],
  );

  const stepBack = useCallback(() => {
    setSession(prev => {
      if (!prev || prev.breadcrumbs.length === 0) return null;
      const crumbs = [...prev.breadcrumbs];
      const last = crumbs.pop()!;
      return {
        metricKey: last.metricKey,
        filters: last.filters,
        displayTitle: last.label,
        breadcrumbs: crumbs,
        openedAt: new Date().toISOString(),
      };
    });
  }, []);

  const close = useCallback(() => setSession(null), []);

  const updateFilters = useCallback((filters: Partial<DrillDownFilters>) => {
    setSession(prev => prev ? { ...prev, filters: { ...prev.filters, ...filters } } : null);
  }, []);

  return (
    <DrillDownContext.Provider value={{
      session,
      isOpen: session !== null,
      open,
      drillInto,
      stepBack,
      close,
      updateFilters,
    }}>
      {children}
    </DrillDownContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────────

export function useDrillDown(): DrillDownContextValue {
  const ctx = useContext(DrillDownContext);
  if (!ctx) throw new Error('useDrillDown must be used within DrillDownProvider');
  return ctx;
}
